/**
 * useCommuneSearch — debounced, abortable commune autocomplete.
 *
 * The React layer over `searchCommunesByName` (communes.ts): keeps options +
 * loading state, debounces keystrokes, and cancels in-flight requests so a slow
 * earlier response can't overwrite a newer query. On any error it falls back to
 * empty options (the caller's typed-fallback path takes over).
 *
 * Used by the INSi `LieuNaissanceAutocomplete`; the birth-place section will
 * reuse it during the modal redesign.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { Commune, searchCommunesByName } from './communes';

export interface UseCommuneSearch {
  /** Result set for the latest settled query (empty until queried). */
  options: Commune[];
  /** True between a query and its response (drives the spinner). */
  loading: boolean;
  /** Feed the user's input; debounced + deduped fetch happens under the hood. */
  setQuery: (query: string) => void;
  /** Drop options/loading and cancel any pending work (e.g. after a selection). */
  clear: () => void;
}

const DEBOUNCE_MS = 250;
/** Don't fire on a single character — too noisy and never selective enough. */
const MIN_QUERY_LENGTH = 2;

export const useCommuneSearch = (): UseCommuneSearch => {
  const [options, setOptions] = useState<Commune[]>([]);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const abortRef = useRef<AbortController | undefined>(undefined);

  const cancelPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = undefined;
    }
  }, []);

  const clear = useCallback(() => {
    cancelPending();
    setOptions([]);
    setLoading(false);
  }, [cancelPending]);

  const setQuery = useCallback(
    (query: string) => {
      const trimmed = (query || '').trim();
      // Supersede whatever is pending/in-flight — its result is now stale.
      cancelPending();

      if (trimmed.length < MIN_QUERY_LENGTH) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      debounceRef.current = setTimeout(() => {
        const controller = new AbortController();
        abortRef.current = controller;

        searchCommunesByName(trimmed, { signal: controller.signal })
          .then((communes) => {
            abortRef.current = undefined;
            setOptions(communes);
            setLoading(false);
          })
          .catch((err) => {
            // A superseded request rejects with AbortError — leave the state to
            // whichever newer call is now in charge.
            if (err && err.name === 'AbortError') return;
            // Any real failure: fall back to no options so the typed-fallback
            // path takes over instead of blocking.
            abortRef.current = undefined;
            setOptions([]);
            setLoading(false);
          });
      }, DEBOUNCE_MS);
    },
    [cancelPending]
  );

  // Cancel any pending timer/request when the field unmounts.
  useEffect(() => cancelPending, [cancelPending]);

  return { options, loading, setQuery, clear };
};

export default useCommuneSearch;
